// Configuration for the browse-collections-api integration (Browse Collections
// on the homepage).
//
// Deliberately separate from src/api/config.ts, src/api/search/config.ts,
// src/api/vpm/config.ts and src/api/videoDetail/config.ts. The browser reaches
// browse-collections-api only through the thecodeword-server proxy - the same
// access decision Igor made for search-agent and video-detail-api - so the host
// is the legacy REACT_APP_BASE_URL and no new base URL belongs here.
//
// The committed defaults are the safe state: 'off' means a build with no env
// changes renders no Browse Collections section at all and issues no request.
// Going live is an env change, never a code change.

// Adapter selection. THREE modes, and the committed default is 'off'.
//
// 'off' is not decoration, and the reason is stronger here than it was for the
// video detail page. NOBODY HAS EVER SEEN AN AUTHENTICATED 200 FROM
// browse-collections-api, in any environment: the contract records the HTTP layer
// as unverified, and as of 2026-08-10 an unauthenticated call still returns a
// Google Front End 403 (IAM enforcing, service reachable). The mock returns
// fabricated covers for all five categories, so a build that defaulted to 'mock'
// would put five invented tiles on the homepage of a deploy nobody had checked -
// a misconfigured build looking like it was working, which is exactly the failure
// this project has already paid for once.
//
// 'off'  -> no fetch, and the section does not render at all.
// 'mock' -> explicit development opt-in against fixtures.
// 'live' -> the real proxy; also requires REACT_APP_COLLECTIONS_PATH.
export const COLLECTIONS_MODE: 'off' | 'mock' | 'live' =
  process.env.REACT_APP_COLLECTIONS_MODE === 'live'
    ? 'live'
    : process.env.REACT_APP_COLLECTIONS_MODE === 'mock'
      ? 'mock'
      : 'off';

// Convenience for the page: whether to render the section and issue requests.
export const COLLECTIONS_ENABLED = COLLECTIONS_MODE !== 'off';

// Proxy path on thecodeword-server, appended to REACT_APP_BASE_URL. The category
// and limit are appended as QUERY parameters by the client.
//
// NO in-code default, deliberately, matching AGENTIC_SEARCH_PATH
// (api/search/config.ts:28) and VIDEO_DETAIL_PATH (api/videoDetail/config.ts:50).
// A default here would make the empty case unreachable, which in turn would make
// the disabled client in ./index.ts dead code that only looks like a safety net.
// Empty in live mode selects the disabled client and fails loudly; it never
// guesses a path.
//
// Consequence for whoever configures a build: live mode needs BOTH
// REACT_APP_COLLECTIONS_MODE=live and REACT_APP_COLLECTIONS_PATH.
export const COLLECTIONS_PATH = process.env.REACT_APP_COLLECTIONS_PATH || '';

// The five categories, exactly as the upstream spells them: case-sensitive,
// exact match, fixed to the client's approved mockup.
//
// Held as a frontend constant rather than fetched from the proxy's taxonomy
// endpoint, so the five LABELS paint on the very first frame with no request and
// no loading state. The row's shape is therefore never data-dependent - only its
// cover images are. If the taxonomy ever changes this constant has to change with
// it, which is a real cost, but the mockup hardcodes five tiles with five labels
// anyway so a sixth category could not render without a design change regardless.
export const COLLECTION_CATEGORIES = ['Sports', 'Music', 'TV/Film', 'Fashion', 'Lifestyle'] as const;

export type CollectionCategory = typeof COLLECTION_CATEGORIES[number];

/**
 * URL slug for a category. 'TV/Film' -> 'tv-film'.
 *
 * THE SLASH IS THE WHOLE REASON THIS EXISTS. '/collections/TV/Film' is two path
 * segments and would never match a ':slug' param - the router would capture 'TV'
 * and 404 on the rest. Percent-encoding does not save it either: a URL in an
 * address bar gets typed, copied and normalised by things we do not control,
 * which is exactly the class of bug that nearly bit the API layer.
 *
 * The slug is DERIVED, never hand-maintained, and the reverse map below is built
 * from COLLECTION_CATEGORIES. There is therefore no second list to fall out of
 * sync: add a category to that constant and both directions follow. The wire
 * value is what the API client always sends; only the URL ever sees a slug.
 */
export const categorySlug = (category: string): string =>
  category.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const CATEGORY_BY_SLUG = new Map<string, CollectionCategory>(
  COLLECTION_CATEGORIES.map((category) => [categorySlug(category), category])
);

/**
 * Resolve a URL slug back to the exact wire value, or null if it is not one of
 * the five. Null is a real answer, not an error: people edit URLs, and the page
 * renders a not-found state without ever issuing a request.
 */
export const categoryFromSlug = (slug: string | undefined): CollectionCategory | null =>
  (slug && CATEGORY_BY_SLUG.get(slug)) || null;

// One video per category is all the row needs: each tile shows a single cover
// image, not a list. Asking for one instead of five cuts the upstream's V4
// signing work fivefold on every homepage load.
export const COLLECTIONS_COVER_LIMIT = 1;

/**
 * What the category PAGE requests: 24 videos first, then 12 more for each click on "view more results", the same sizes as search.
 *
 * Until 3 Nov 2026 the page asked once for 50 and stopped, so past 50 a video could never be reached (prod: TV/Film holds 2,799 Ready
 * videos, Music 1,502, Sports 582, Lifestyle 514, Fashion 243). The service now returns `next_cursor` and takes it back as `cursor`
 * (VPM #127, gateway #31), so the page walks a category to its end. Both are ceilings, not fetch sizes: the service returns
 * min(limit, what is left) and signs pictures only for what it returns.
 *
 * The envelope is { category, count, limit, total, next_cursor, videos }. `total` is the category-wide count and the count line reads
 * it; `next_cursor` is null on the last page, and that is the only thing that ends the walk.
 */
export const COLLECTIONS_FIRST_PAGE_LIMIT = 24;
export const COLLECTIONS_MORE_PAGE_LIMIT = 12;

// Client hang backstop. Deliberately ABOVE the proxy's own 30s outbound budget
// (BROWSE_COLLECTIONS_API_TIMEOUT_MS on thecodeword-server) so the proxy always
// resolves first - with a 200, or with its own 502/504 - and this client never
// gives up on a request that is still legitimately running. Same layering rule as
// VIDEO_DETAIL_TIMEOUT_MS sitting above the video-detail proxy's 30s, and
// AGENTIC_TIMEOUT_MS sitting above search-agent's 60s ceiling.
export const COLLECTIONS_TIMEOUT_MS = 45_000;

// How long a fetched row stays trustworthy. Every URL the upstream emits is
// signed with X-Goog-Expires=3600, so 45 minutes leaves a comfortable margin for
// a slow refetch before anything expires.
//
// This matters MORE here than on the video detail page. A detail page is visited
// and left; a homepage is the tab people leave open all afternoon. Past the hour
// the cover images 404 and the tiles go blank with no JavaScript error of any
// kind, because a failed <img> is not an exception. The visibility-based refetch
// in useCollections is what prevents that.
export const COLLECTIONS_TTL_MS = 45 * 60 * 1000;
